/*
 * Crafting A-Z — pillar & silo switcher
 *
 * A standard tabs widget: the pillar list is the tablist, each pillar's content
 * is a tabpanel, and only the chosen one is visible. All panels are in the HTML
 * already — this file only toggles `hidden`, so the silo links stay crawlable
 * and a visitor with no JavaScript still gets the first pillar rather than a
 * blank panel.
 *
 * Keyboard follows the WAI-ARIA tabs pattern, which is what a vertical tablist
 * is judged against: Up/Down (and Left/Right, since the list turns horizontal on
 * mobile) move between pillars, Home/End jump to the ends, and roving tabindex
 * keeps the whole list one Tab stop instead of seventeen.
 *
 * Re-runs on shopify:section:load and :select so the theme editor behaves —
 * selecting a pillar block in the sidebar opens that pillar.
 */
(function () {
  'use strict';

  var SELECTOR = '[data-ch-pillars]';

  function partsOf(widget) {
    return {
      tabs: Array.prototype.slice.call(widget.querySelectorAll('[data-ch-tab]')),
      panels: Array.prototype.slice.call(widget.querySelectorAll('[data-ch-panel]'))
    };
  }

  function activate(widget, index, options) {
    var parts = partsOf(widget);
    if (index < 0 || index >= parts.tabs.length) return;

    parts.tabs.forEach(function (tab, i) {
      var on = i === index;
      tab.setAttribute('aria-selected', on ? 'true' : 'false');
      tab.setAttribute('tabindex', on ? '0' : '-1');
      tab.classList.toggle('is-active', on);
    });

    parts.panels.forEach(function (panel, i) {
      if (i === index) {
        panel.removeAttribute('hidden');
      } else {
        panel.setAttribute('hidden', '');
      }
    });

    if (options && options.focus) {
      parts.tabs[index].focus();

      /* Keep the chosen pillar in view in its own scroller. `nearest` so a tab
         that is already visible does not jolt the list, and `block: 'nearest'`
         so the page itself never scrolls — moving focus in a tab list should
         not move the reader away from the panel they are reading. */
      if (parts.tabs[index].scrollIntoView) {
        parts.tabs[index].scrollIntoView({ block: 'nearest', inline: 'nearest' });
      }
    }
  }

  function init(widget) {
    if (!widget || widget.dataset.chPillarsReady === 'true') return;

    var parts = partsOf(widget);
    if (parts.tabs.length === 0) return;

    widget.dataset.chPillarsReady = 'true';

    parts.tabs.forEach(function (tab, i) {
      tab.addEventListener('click', function () {
        activate(widget, i);
      });

      tab.addEventListener('keydown', function (event) {
        var last = parts.tabs.length - 1;
        var next = null;

        switch (event.key) {
          case 'ArrowDown':
          case 'ArrowRight':
            next = i === last ? 0 : i + 1;
            break;
          case 'ArrowUp':
          case 'ArrowLeft':
            next = i === 0 ? last : i - 1;
            break;
          case 'Home':
            next = 0;
            break;
          case 'End':
            next = last;
            break;
          default:
            return;
        }

        event.preventDefault();
        activate(widget, next, { focus: true });
      });
    });

    /* Whatever the markup shipped as selected wins, so the server-rendered and
       the scripted state never disagree on first paint. */
    var initial = parts.tabs.findIndex(function (tab) {
      return tab.getAttribute('aria-selected') === 'true';
    });
    activate(widget, initial === -1 ? 0 : initial);
  }

  function initAll(root) {
    var scope = root && root.querySelectorAll ? root : document;
    Array.prototype.forEach.call(scope.querySelectorAll(SELECTOR), init);
  }

  /* Theme editor: selecting a pillar block should open that pillar, otherwise a
     merchant edits a panel they cannot see. */
  function openSelectedBlock(event) {
    var block = event.target;
    var widget = block.closest ? block.closest(SELECTOR) : null;
    if (!widget) return;

    var tabs = partsOf(widget).tabs;
    var index = tabs.indexOf(block);
    if (index !== -1) activate(widget, index);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', function (event) {
    initAll(event.target);
  });

  document.addEventListener('shopify:block:select', openSelectedBlock);
})();
